<template>
    <div class="navbar">
        <NavbarLogo :is-visible="!isSubmenuOpen" @close-menu="closeMenu" />
        <NavbarBack :is-visible="isSubmenuOpen" @click="openMenuIndex = null" />
        <NavbarLabel :label="currentMenuLabel" :is-visible="isSubmenuOpen" />
        <div class="navbar__trailing" data-testid="navbar-trailing">
            <NavbarSignupCta class="navbar__trailing-cta" />
            <NavbarBurger :is-open="isMenuOpen" :is-submenu-visible="isSubmenuOpen" @click="isMenuOpen = !isMenuOpen" />
        </div>
        <NavbarContent :is-open="isMenuOpen" :nav="config" :open-menu-index="openMenuIndex" @toggle-menu="toggleMenu" @close-menu="closeMenu" />
    </div>
</template>

<script setup lang="ts">
    import {computed, ref, type Ref, watch} from 'vue';
    import type {NavbarConfig} from '@navbar-unlogged-types';
    import NavbarBack from './NavbarBack.vue';
    import NavbarBurger from './NavbarBurger.vue';
    import NavbarContent from './NavbarContent.vue';
    import NavbarLabel from './NavbarLabel.vue';
    import NavbarLogo from './NavbarLogo.vue';
    import NavbarSignupCta from './NavbarSignupCta.vue';
    import {useNavbarConfig} from '../composables/useNavbarConfig';
    import {useNavbarMode} from '../composables/useNavbarMode';

    const {config: moduleConfig, currentModeConfig} = useNavbarMode();
    const {getConfigForDocument, loadAllNavbarConfigs} = useNavbarConfig();

    const documentIds = moduleConfig.value.modes?.map((mode) => mode.prismicDocumentId) ?? [];
    await loadAllNavbarConfigs(documentIds);

    const config = computed(() => getConfigForDocument(currentModeConfig.value?.prismicDocumentId));

    const {isMenuOpen, openMenuIndex, isSubmenuOpen, currentMenuLabel, toggleMenu, closeMenu} = manageNavbarMenu(config);

    function manageNavbarMenu(nav: Ref<NavbarConfig>) {
        const isMenuOpen = ref(false);
        const openMenuIndex = ref<number | null>(null);
        const isSubmenuOpen = computed(() => openMenuIndex.value !== null);
        const currentMenuLabel = computed(() => {
            if (openMenuIndex.value !== null && nav.value?.items[openMenuIndex.value]) {
                return nav.value?.items[openMenuIndex.value]!.label ?? '';
            }
            return '';
        });

        watch(isMenuOpen, (open) => {
            document.body.classList.toggle('menu-open', open);
            if (!open) {
                openMenuIndex.value = null;
            }
        });

        const toggleMenu = (index: number) => {
            openMenuIndex.value = openMenuIndex.value === index ? null : index;
        };

        const closeMenu = () => {
            isMenuOpen.value = false;
            openMenuIndex.value = null;
        };

        return {
            isMenuOpen,
            openMenuIndex,
            isSubmenuOpen,
            currentMenuLabel,
            toggleMenu,
            closeMenu,
        };
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    /* mobile-first */
    .navbar {
        display: flex;
        align-items: flex-end;
        height: var(--navbar-height);
        line-height: 1;
        gap: var(--joy-core-spacing-3);
        position: relative;
        -webkit-tap-highlight-color: transparent;

        &__trailing {
            margin-left: auto;
            display: flex;
        }

        &__trailing-cta {
            display: flex;
            align-items: center;
        }

        &:has(.navbar__content--open) .navbar__trailing-cta {
            display: none;
        }
    }

    /* desktop */
    @include mq.screen_sm(min) {
        .navbar {
            &__trailing {
                display: none;
            }
        }
    }
</style>
